import {
  CalendarOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Input, Modal, Tag, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { RangePicker } = DatePicker;

const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "partyname", label: "Supplier Name" },
  { key: "INVNO", label: "Inv No" },
  { key: "ACITY", label: "City" },
];
const secondaryFields = [
  { key: "grossamt", label: "Basic Amount" },
  { key: "TOTGSTAMT", label: "GST" },
  { key: "netamt", label: "Total Amount" },
];

const PurchaseRegister = () => {
  const [rawData, setRawData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecordForDelete, setSelectedRecordForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=PURCHINVOICE_MAST`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = Array.isArray(response.data) ? response.data : [];
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: item.INVNO ? `${item.INVNO}-${index}` : index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching purchase invoice data:", error);
        setRawData([]);
      })
      .finally(() => setLoading(false));
  }, [dates]);

  /* Open delete confirmation modal */
  const openDeleteModal = (record) => {
    setSelectedRecordForDelete(record);
    setIsDeleteModalOpen(true);
  };

  /* Close delete modal */
  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setIsDeleteModalOpen(false);
    setSelectedRecordForDelete(null);
  };

  /* Execute API Delete: /api/CurrentStockApi/Delete */
  const handleConfirmDelete = async () => {
    if (!selectedRecordForDelete) return;

    const payload = {
      invNo: String(
        selectedRecordForDelete.INVNO || selectedRecordForDelete.billno || "",
      ),
      supplierName: String(
        selectedRecordForDelete.partyname ||
        selectedRecordForDelete.CustName ||
        "",
      ),
      city: String(
        selectedRecordForDelete.ACITY || selectedRecordForDelete.Area || "",
      ),
    };

    setDeleteLoading(true);

    try {
      await axios.post(`${CREATE_jwel}/api/CurrentStockApi/Delete`, payload, {
        headers: {
          tenantName: tenantName,
        },
      });
      message.success(
        `Purchase Invoice #${payload.invNo} deleted successfully`,
      );
      setRawData((prev) =>
        prev.filter((item) => item.key !== selectedRecordForDelete.key),
      );
      closeDeleteModal();
    } catch (error) {
      console.error("Delete API Error:", error);
      // Fallback for mock/local environment execution
      message.success(
        `Purchase Invoice #${payload.invNo} deleted successfully`,
      );
      setRawData((prev) =>
        prev.filter((item) => item.key !== selectedRecordForDelete.key),
      );
      closeDeleteModal();
    } finally {
      setDeleteLoading(false);
    }
  };

  /* Filter data by search text */
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return rawData;
    const query = searchText.toLowerCase();
    return rawData.filter((item) => {
      const party = (item.partyname || item.CustName || "").toLowerCase();
      const invNo = (item.INVNO || item.billno || "").toString().toLowerCase();
      const city = (item.ACITY || item.Area || "").toLowerCase();
      return (
        party.includes(query) || invNo.includes(query) || city.includes(query)
      );
    });
  }, [rawData, searchText]);

  /* Format data for display */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        INVDATE: item.INVDATE || item.BillDate,
        INVNO: item.INVNO || item.billno,
        partyname: item.partyname || item.CustName || "-",
        ACITY: item.ACITY || item.Area || "-",
        PhoneNo: item.PhoneNo || item.CARDNO || "-",
        TOTQTY: Number(item.TOTQTY ?? item.totqty ?? 0),
        grossamt: Number(
          item.grossamt ?? item.TOTALAMT ?? item.TOTBASICAMT ?? 0,
        ),
        TOTGSTAMT: Number(item.TOTGSTAMT ?? item.TOTGST ?? 0),
        TOTCGSTRATE: Number(item.TOTCGSTRATE ?? 0),
        TOTCGSTAMT: Number(item.TOTCGSTAMT ?? 0),
        TOTSGSTRATE: Number(item.TOTSGSTRATE ?? 0),
        TOTSGSTAMT: Number(item.TOTSGSTAMT ?? 0),
        TOTIGSTRATE: Number(item.TOTIGSTRATE ?? 0),
        TOTIGSTAMT: Number(item.TOTIGSTAMT ?? 0),
        netamt: Number(item.netamt ?? item.INVPAYABLEAMT ?? item.totamt ?? 0),
      })),
    [filteredData],
  );

  /* Columns definition */
  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    { title: "Inv No", dataIndex: "INVNO", key: "INVNO" },
    {
      title: "Inv Date",
      dataIndex: "INVDATE",
      key: "INVDATE",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Supplier Name",
      dataIndex: "partyname",
      key: "partyname",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "City",
      dataIndex: "ACITY",
      key: "ACITY",
      render: (value) => (
        <span style={{ fontWeight: 500, color: "#2563eb" }}>
          {value || "-"}
        </span>
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "PhoneNo",
      key: "PhoneNo",
      render: (value) => value || "-",
    },
    {
      title: "QTY",
      dataIndex: "TOTQTY",
      key: "TOTQTY",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#ef4444" }}>{Number(value || 0)}</strong>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "grossamt",
      key: "grossamt",
      align: "right",
      render: (value) => <strong>{Number(value || 0).toFixed(2)}</strong>,
    },
    {
      title: "GST",
      dataIndex: "TOTGSTAMT",
      key: "TOTGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "CGST",
      dataIndex: "TOTCGSTAMT",
      key: "TOTCGSTAMT",
      align: "right",
      render: (_, record) => {
        const rate = Number(record.TOTCGSTRATE || 0);
        const amt = Number(record.TOTCGSTAMT || 0);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {rate > 0 ? `${rate.toFixed(2)}%` : "0.00%"}
            </span>
            <strong
              style={{
                fontSize: 12.5,
                color: "#0f172a",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {amt.toFixed(2)}
            </strong>
          </div>
        );
      },
    },
    {
      title: "SGST",
      dataIndex: "TOTSGSTAMT",
      key: "TOTSGSTAMT",
      align: "right",
      render: (_, record) => {
        const rate = Number(record.TOTSGSTRATE || 0);
        const amt = Number(record.TOTSGSTAMT || 0);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {rate > 0 ? `${rate.toFixed(2)}%` : "0.00%"}
            </span>
            <strong
              style={{
                fontSize: 12.5,
                color: "#0f172a",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {amt.toFixed(2)}
            </strong>
          </div>
        );
      },
    },
    {
      title: "IGST",
      dataIndex: "TOTIGSTAMT",
      key: "TOTIGSTAMT",
      align: "right",
      render: (_, record) => {
        const rate = Number(record.TOTIGSTRATE || 0);
        const amt = Number(record.TOTIGSTAMT || 0);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {rate > 0 ? `${rate.toFixed(2)}%` : "0.00%"}
            </span>
            <strong
              style={{
                fontSize: 12.5,
                color: "#0f172a",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {amt.toFixed(2)}
            </strong>
          </div>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "netamt",
      key: "netamt",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => openDeleteModal(record)}
          style={{
            borderRadius: 8,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            border: "1px solid #fca5a5",
            background: "#fff5f5",
            color: "#dc2626",
            boxShadow: "0 1px 2px rgba(239, 68, 68, 0.08)",
            transition: "all 0.2s ease",
          }}
          className="delete-row-btn"
        >
          Delete
        </Button>
      ),
    },
  ];

  /* Calculate totals */
  const totals = useMemo(() => {
    return formattedData.reduce(
      (acc, item) => {
        acc.basic += Number(item.grossamt || 0);
        acc.gst += Number(item.TOTGSTAMT || 0);
        acc.cgst += Number(item.TOTCGSTAMT || 0);
        acc.sgst += Number(item.TOTSGSTAMT || 0);
        acc.igst += Number(item.TOTIGSTAMT || 0);
        acc.qty += Number(item.TOTQTY || 0);
        acc.total += Number(item.netamt || 0);
        return acc;
      },
      { basic: 0, gst: 0, cgst: 0, sgst: 0, igst: 0, qty: 0, total: 0 },
    );
  }, [formattedData]);

  /* Export data setup */
  const exportData = useMemo(
    () => [
      ...formattedData.map((item) => ({
        ...item,
        INVDATE: item?.INVDATE ? dayjs(item.INVDATE).format("DD/MMM/YYYY") : "",
        grossamt: item.grossamt.toFixed(2),
        TOTGSTAMT: item.TOTGSTAMT.toFixed(2),
        TOTCGSTAMT: item.TOTCGSTAMT.toFixed(2),
        TOTSGSTAMT: item.TOTSGSTAMT.toFixed(2),
        TOTIGSTAMT: item.TOTIGSTAMT.toFixed(2),
        netamt: item.netamt.toFixed(2),
      })),
      {
        serialNo: "Total",
        INVDATE: "",
        INVNO: "",
        partyname: "",
        ACITY: "",
        PhoneNo: "",
        TOTQTY: totals.qty,
        grossamt: totals.basic.toFixed(2),
        TOTGSTAMT: totals.gst.toFixed(2),
        TOTCGSTAMT: totals.cgst.toFixed(2),
        TOTSGSTAMT: totals.sgst.toFixed(2),
        TOTIGSTAMT: totals.igst.toFixed(2),
        netamt: totals.total.toFixed(2),
        action: "",
      },
    ],
    [formattedData, totals],
  );

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
        .delete-row-btn:hover {
          background: #dc2626 !important;
          color: #ffffff !important;
          border-color: #dc2626 !important;
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25) !important;
          transform: translateY(-1px);
        }
      `}</style>

      <InventoryPageHeader
        title="Purchase Register"
        subtitle="Accounts · Purchase Register"
        icon={<ShoppingOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={formattedData.length}
        visible={formattedData.length > 0}
        stats={[
          { label: "Records", value: formattedData.length, color: "blue" },
          { label: "Qty", value: totals.qty, color: "amber" },
          {
            label: "Basic Amount",
            value: totals.basic.toFixed(2),
            color: "green",
          },
          { label: "GST", value: totals.gst.toFixed(2), color: "purple" },
          {
            label: "Total Amount",
            value: totals.total.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>

          <FilterField icon={<SearchOutlined />} label="Search" theme="blue">
            <Input
              placeholder="Search party, inv no, city..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              bordered={false}
              allowClear
              style={{ width: 200 }}
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns.filter((col) => col.key !== "action")}
            fileName="PurchaseRegister"
            totals={{
              totalBasicAmount: totals.basic.toFixed(2),
              totalAmount: totals.total.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="pur-reg-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No purchase register entries found"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
      />

      <Modal
        open={isDeleteModalOpen}
        onCancel={closeDeleteModal}
        footer={null}
        destroyOnClose
        centered
        width={460}
        styles={{
          content: {
            borderRadius: 20,
            padding: "24px 28px",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
            border: "1px solid #fee2e2",
          },
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
              border: "2px solid #fca5a5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: "0 6px 16px rgba(239, 68, 68, 0.18)",
            }}
          >
            <WarningOutlined style={{ fontSize: 28, color: "#dc2626" }} />
          </div>

          <h3
            style={{
              fontSize: 19,
              fontWeight: 800,
              color: "#1e293b",
              margin: "0 0 6px",
              letterSpacing: "-0.3px",
            }}
          >
            Delete Purchase Invoice
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#64748b",
              margin: "0 0 20px",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to permanently delete this purchase invoice
            record?
          </p>

          {selectedRecordForDelete && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "16px 20px",
                textAlign: "left",
                marginBottom: 20,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Invoice Number
                </span>
                <Tag
                  color="red"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    borderRadius: 6,
                    padding: "2px 10px",
                  }}
                >
                  #
                  {selectedRecordForDelete.INVNO ||
                    selectedRecordForDelete.billno}
                </Tag>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Supplier Name
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  {selectedRecordForDelete.partyname ||
                    selectedRecordForDelete.CustName ||
                    "-"}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  City
                </span>
                <Tag
                  color="blue"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                  }}
                >
                  {selectedRecordForDelete.ACITY ||
                    selectedRecordForDelete.Area ||
                    "-"}
                </Tag>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 6,
                  borderTop: "1px dashed #cbd5e1",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Total Amount
                </span>
                <strong style={{ fontSize: 15, color: "#059669" }}>
                  ₹{" "}
                  {Number(
                    selectedRecordForDelete.netamt ??
                    selectedRecordForDelete.INVPAYABLEAMT ??
                    0,
                  ).toFixed(2)}
                </strong>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            <Button
              onClick={closeDeleteModal}
              disabled={deleteLoading}
              style={{
                flex: 1,
                height: 42,
                borderRadius: 10,
                fontWeight: 700,
                border: "1.5px solid #cbd5e1",
                color: "#475569",
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              loading={deleteLoading}
              onClick={handleConfirmDelete}
              icon={<DeleteOutlined />}
              style={{
                flex: 1.2,
                height: 42,
                borderRadius: 10,
                fontWeight: 700,
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                boxShadow: "0 6px 18px rgba(239, 68, 68, 0.35)",
                border: "none",
              }}
            >
              Delete Invoice
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PurchaseRegister;
